import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Tabs, Tab, Stack, Avatar, Chip,
  IconButton, Breadcrumbs, Link, Divider, Grid, Card, CardContent,
  Button, CircularProgress, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookIcon from '@mui/icons-material/Book';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import QuizIcon from '@mui/icons-material/Quiz';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GradeIcon from '@mui/icons-material/Grade';
import DescriptionIcon from '@mui/icons-material/Description';
import { fetchSchedulesBySubject } from '../../slices/scheduleSlice';
import { fetchClasses } from '../../slices/classesSlice';
import SubjectOverviewTab from './SubjectOverviewTab';
import SubjectScheduleTab from './SubjectScheduleTab';
import SubjectStatisticsTab from './SubjectStatisticsTab';
// Import academic tab components
import ExamsTab from '../classTabs/subjectTabs/ExamsTab';
import HomeworkTab from '../classTabs/subjectTabs/HomeworkTab';
import GradesTab from '../classTabs/subjectTabs/GradesTab';

const SubjectDetailView = ({ subject, onBack }) => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const userRole = localStorage.getItem('role');
  const isStudent = userRole === 'student';
  
  // Get student's class ID from user info
  const studentClassId = useMemo(() => {
    if (isStudent) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.school_class_id || user.school_class?.id || user.school_class;
    }
    return null;
  }, [isStudent]);
  
  const { classes } = useSelector((state) => ({
    classes: state.classes.classes || []
  }));

  useEffect(() => {
    if (subject?.id) {
      loadSchedules();
    }
    // Load classes if not already loaded (but not for students)
    if (!isStudent && classes.length === 0) {
      dispatch(fetchClasses());
    }
  }, [subject?.id, isStudent, studentClassId]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchSchedulesBySubject(subject.id)).unwrap();
      let filteredSchedules = response || [];
      
      // For students, filter schedules to only show their class
      if (isStudent && studentClassId) {
        filteredSchedules = filteredSchedules.filter(schedule => {
          const scheduleClassId = schedule.school_class || schedule.school_class_id;
          return String(scheduleClassId) === String(studentClassId);
        });
      }
      
      setSchedules(filteredSchedules);
    } catch (error) {
      console.error('Failed to load schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectColor = (subjectName) => {
    const colors = {
      'رياضيات': '#2196F3', 'علوم': '#4CAF50', 'فيزياء': '#FF9800',
      'كيمياء': '#9C27B0', 'احياء': '#8BC34A', 'عربي': '#F44336',
      'انجليزي': '#3F51B5', 'تاريخ': '#795548', 'جغرافيا': '#607D8B',
      'تربية': '#E91E63', 'رياضة': '#FF5722', 'فنون': '#673AB7',
      'موسيقى': '#9E9E9E', 'حاسوب': '#00BCD4'
    };
    const key = Object.keys(colors).find(k => subjectName?.includes(k));
    return key ? colors[key] : '#757575';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const subjectColor = getSubjectColor(subject?.name);

  // Get the class object from subject data
  const selectedClass = subject?.school_class 
    ? (typeof subject.school_class === 'object' 
        ? subject.school_class 
        : classes.find(c => c.id === subject.school_class || c.id === subject.school_class_id))
    : null;

  // If subject has school_class_id but not school_class object, find it
  const classFromId = subject?.school_class_id 
    ? classes.find(c => c.id === subject.school_class_id)
    : null;

  const finalSelectedClass = selectedClass || classFromId || {
    id: subject?.school_class || subject?.school_class_id,
    name: subject?.school_class_name || 'غير محدد'
  };

  const tabs = [
    { label: 'نظرة عامة', icon: <BookIcon />, component: SubjectOverviewTab },
    { label: 'الاختبارات والدرجات', icon: <QuizIcon />, component: ExamsTab },
    { label: 'الواجبات المنزلية', icon: <AssignmentIcon />, component: HomeworkTab },
    { label: 'الجدول الزمني', icon: <CalendarTodayIcon />, component: SubjectScheduleTab },
    { label: 'الدرجات', icon: <GradeIcon />, component: GradesTab },
    { label: 'الإحصائيات', icon: <TrendingUpIcon />, component: SubjectStatisticsTab },
  ];

  const ActiveTabComponent = tabs[currentTab]?.component || SubjectOverviewTab;

  return (
    <Box sx={{ direction: 'rtl' }}>
      {/* Header Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${subjectColor}15 0%, ${subjectColor}05 100%)`,
          border: `1px solid ${subjectColor}30`
        }}
      >
        <Stack useFlexGap direction="row" spacing={2} alignItems="center" mb={2}>
          <IconButton onClick={onBack} sx={{ color: subjectColor }}>
            <ArrowBackIcon />
          </IconButton>
          <Breadcrumbs>
            <Link
              component="button"
              variant="body1"
              onClick={onBack}
              sx={{ textDecoration: 'none', color: 'text.secondary' }}
            >
              المواد الدراسية
            </Link>
            <Typography color="text.primary" fontWeight="bold">
              {subject?.name}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack useFlexGap direction="row" spacing={3} alignItems="center">
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: subjectColor,
                  fontSize: '2rem',
                  fontWeight: 'bold'
                }}
              >
                {getInitials(subject?.name)}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  {subject?.name}
                </Typography>
                <Stack useFlexGap direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                  {subject?.school_class_name && (
                    <Chip
                      icon={<SchoolIcon />}
                      label={subject.school_class_name}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {subject?.teacher_name && (
                    <Chip
                      icon={<PersonIcon />}
                      label={subject.teacher_name}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {subject?.hours_per_week && (
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`${subject.hours_per_week} ساعة/أسبوع`}
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 60,
              fontSize: '1rem',
              fontWeight: 'bold',
              '&.Mui-selected': { color: subjectColor }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: subjectColor
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {loading && currentTab === 3 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ActiveTabComponent
              subject={subject}
              schedules={schedules}
              subjectColor={subjectColor}
              onRefresh={loadSchedules}
              selectedClass={finalSelectedClass}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SubjectDetailView;
